"use client";

import { signIn } from "next-auth/react";
import { ChevronLeft } from "lucide-react";
import Link from "next/link";
import { Button } from "../ui/button";
import Icons from "../ui/icons";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import AnimationContainer from "../utils/animation-container";
import { toast } from "sonner";
import { useState } from "react";
import { AnimatedCircularProgress, OtpInput } from "../index";
import { WrapBg } from '../utils/WrapBg';
import { useSearchParams } from "next/navigation";

const RegisterSection = () => {
    const searchParams = useSearchParams();
    const refCode = searchParams.get('ref');

    const [step, setStep] = useState(1);
    const [email, setEmail] = useState("");
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [otp, setOtp] = useState("");
    const [loading, setLoading] = useState(false);

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        if (password !== confirmPassword) {
            toast.error("خطا", { description: "رمز عبور و تکرار آن یکسان نیستند!" });
            setLoading(false);
            return;
        }

        const res = await fetch("/api/auth/signup", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ username, email, password, refCode }),
        });

        const data = await res.json();
        setLoading(false);

        if (res.ok) {
            toast.custom(
                (t: any) => (
                    <div className="flex items-center gap-3 bg-purple-800/10 rounded-lg p-4 border-[1px]  border-purple-900/30">
                        <AnimatedCircularProgress
                            duration={120}
                            onComplete={() => {
                                setStep(1);
                                toast.error("⏳ مهلت تمام شد!", {
                                    description: "لطفاً دوباره درخواست کد بدهید.",
                                    position: "top-right",
                                });
                                toast.dismiss(t.id);
                            }}
                        />
                        <div className="text-sm">کد تأیید ارسال شد، لطفاً وارد کنید.</div>
                    </div>
                ),
                { duration: 120000, id: "email-otp-toast", position: "top-right" }
            );
            setTimeout(() => setStep(2), 0);
        } else {
            toast.error("خطا", { description: data.error || "مشکلی در ثبت‌نام رخ داد." });
        }
    };

    const handleVerifyOtp = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        const res = await signIn("email-otp", {
            email,
            code: otp,
            redirect: false,
        });

        setLoading(false);

        if (res?.error) {
            toast.error("خطا", { description: "کد تأیید نامعتبر یا منقضی شده است." });
        } else {
            toast.dismiss("email-otp-toast");
            toast.success("ورود موفق", { description: "در حال هدایت به داشبورد..." });
            window.location.href = "/dashboard";
        }
    };

    return (
        <section className="flex flex-col items-center justify-center w-full relative">
            <div className="bg-background grid grid-cols-1 min-h-screen lg:h-screen overflow-y-hidden scrollbar-hide lg:grid-cols-2 w-full">
                <AnimationContainer reverse className="absolute top-4 md:top-8 left-4 md:left-8 z-50">
                    <div className="flex items-center">
                        <Button size="sm" variant="outline" asChild>
                            <Link href="/">
                                برگشت
                                <ChevronLeft className="w-4 h-4 mr-1" />
                            </Link>
                        </Button>
                    </div>
                </AnimationContainer>

                <AnimationContainer delay={0.1}>
                    <div
                        style={{
                            backgroundImage: "url('/images/bg-4.jpg')",
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            backgroundRepeat: "no-repeat",

                        }}
                        className="bg-muted/30 relative h-full w-full hidden lg:block">
                        <div className="flex w-4/5 mx-auto lg:px-8 items-center justify-center h-full">
                            <WrapBg />
                        </div>
                        <div className="flex flex-col items-center absolute left-1/2 bottom-[15%] -translate-x-1/2 translate-y-1/2 justify-center max-w-sm">
                            <h1 className="text-xl md:text-2xl font-semibold text-center">
                                با مخاطبان خود ارتباط برقرار کنید
                            </h1>
                            <p className="text-muted-foreground mt-2 text-sm md:text-base text-center">
                                با بازی تور، می‌توانید با مخاطبان خود ارتباط برقرار کرده و اثر برند خود را رشد دهید.
                            </p>
                            <Button size="sm" variant="white" className="mt-4">
                                <Link href="/blog">بیشتر یاد بگیرید</Link>
                            </Button>
                        </div>
                    </div>
                </AnimationContainer>

                <AnimationContainer delay={0.2}>
                    <div className="flex mt-16 justify-center py-12 relative px-4 md:px-0">
                        <div className="mx-auto grid max-w-sm w-full gap-6">
                            <div className="flex flex-col gap-4 text-start">
                                <h1 className="text-2xl font-semibold">{step === 1 ? "بریم که شروع کنیم" : "کد تأیید را وارد کنید"}</h1>
                                <p className="text-muted-foreground">{step === 1
                                    ? "لطفا اطلاعات لازم را پر کنید"
                                    : "کد ارسال شده به ایمیل خود را وارد کنید."}
                                </p>
                            </div>
                            {step === 1 ? (
                                <form onSubmit={handleRegister} className="grid gap-4 mt-2 w-full">
                                    <div className="grid gap-2">
                                        <Label htmlFor="username">نام کاربری</Label>
                                        <Input
                                            required
                                            id="username"
                                            type="text"
                                            placeholder="یوزرنیم شما"
                                            value={username}
                                            onChange={(e) => setUsername(e.target.value)}
                                        />
                                    </div>
                                    <div className="grid gap-2">
                                        <Label htmlFor="email">ایمیل</Label>
                                        <Input
                                            required
                                            id="email"
                                            type="email"
                                            placeholder="آدرس ایمیل شما"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                        />
                                    </div>
                                    <div className="grid gap-2">
                                        <Label htmlFor="password">رمز عبور</Label>
                                        <Input
                                            required
                                            id="password"
                                            type="password"
                                            placeholder="رمز عبور شما"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                        />
                                    </div>
                                    <div className="grid gap-2">
                                        <Label htmlFor="re-password">تکرار رمز عبور</Label>
                                        <Input
                                            required
                                            id="re-password"
                                            type="password"
                                            placeholder="رمز عبور خود را تکرار کنید"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                        />
                                    </div>
                                    <Button type="submit" className="w-full" disabled={loading}>
                                        {loading ? "در حال ثبت‌نام..." : "ساخت اکانت"}
                                    </Button>
                                </form>
                            ) : (
                                <>
                                    <form onSubmit={handleVerifyOtp} className="grid gap-4 mt-2 w-full">
                                        <div className="grid gap-2">
                                            <Label htmlFor="code">کد تأیید</Label>
                                            <OtpInput onChange={setOtp} />
                                        </div>
                                        <Button type="submit" className="w-full" disabled={loading}>
                                            {loading ? "در حال بررسی..." : "تأیید کد"}
                                        </Button>
                                    </form>
                                    <div className="mt-4 text-center text-sm">
                                        ایمیل را اشتباه وارد کردید؟{" "}
                                        <button
                                            className="text-primaryLight"
                                            onClick={() => {
                                                toast.dismiss("email-otp-toast");
                                                setStep(1)
                                            }}
                                        >
                                            اصلاح ایمیل
                                        </button>
                                    </div>
                                </>
                            )}





                            <div className="flex items-center justify-center w-full my-2">
                                <div className="h-px bg-border w-full"></div>
                                <p className="text-muted-foreground mx-2">یا</p>
                                <div className="h-px bg-border w-full"></div>
                            </div>
                            <Button onClick={() => signIn("google")} variant="subtle" className="w-full">
                                <Icons.google className="w-4 h-4 ml-2" />
                                ثبت نام با گوگل
                            </Button>
                            <Link href="/auth/verify-phone">
                                <Button variant="subtle" className="w-full">
                                    <Icons.call className="w-5 h-5 ml-2" />
                                    ورود با شماره تلفن
                                </Button>
                            </Link>
                            <div className="mt-4 text-center text-sm">
                                قبلاً ثبت نام کرده‌اید؟{" "}
                                <Link href="/auth/signin" className="text-primaryLight">
                                    ورود
                                </Link>
                            </div>
                        </div>
                    </div>
                </AnimationContainer>
            </div>
        </section>
    );
};

export default RegisterSection;
